/**
 * ErrorMessage Component - Story 7.6: Add Loading and Error States
 * Reusable error display component with user-friendly messages
 *
 * Handles different error scenarios:
 * - Network errors: Connection problem
 * - 404 errors: Product not found
 * - 429 errors: Rate limiting
 * - 409 errors: Duplicate inquiry
 * - 500 errors: Server error
 * - Generic errors: Fallback message
 */

import React from 'react';
import { AlertCircle, AlertTriangle, Wifi, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

export interface ErrorMessageProps {
  /** Error status code */
  statusCode?: number;
  /** Error message from API or custom message */
  message?: string;
  /** Callback function to retry the failed request */
  onRetry?: () => void;
  /** Whether retry is in progress */
  isRetrying?: boolean;
  /** CSS class name for styling */
  className?: string;
  /** Error variant: 'default', 'compact', or 'inline' */
  variant?: 'default' | 'compact' | 'inline';
  /** Custom error code for specific handling */
  errorCode?: string;
}

/**
 * Get user-friendly error message based on status code
 */
const getErrorMessage = (statusCode?: number, customMessage?: string, errorCode?: string): string => {
  if (customMessage && customMessage.trim()) {
    return customMessage;
  }

  switch (statusCode) {
    case 404:
      return 'Product not found.';
    case 429:
      return 'Too many requests. Please try again in a moment.';
    case 409:
      return 'You already inquired about this product.';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'Something went wrong. Please try again later.';
    case 0:
    case undefined:
      // Network error or no status code
      if (customMessage?.toLowerCase().includes('network') ||
          customMessage?.toLowerCase().includes('connection') ||
          customMessage?.toLowerCase().includes('fetch')) {
        return 'Connection problem. Please check your internet connection and try again.';
      }
      return 'Connection problem. Please try again.';
    default:
      return customMessage || 'Something went wrong. Please try again.';
  }
};

/**
 * Get icon based on error type
 */
const getErrorIcon = (statusCode?: number): React.ReactNode => {
  switch (statusCode) {
    case 404:
      return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
    case 429:
      return <Clock className="h-5 w-5 text-orange-600" />;
    case 0:
    case undefined:
      return <Wifi className="h-5 w-5 text-red-600" />;
    default:
      return <AlertCircle className="h-5 w-5 text-red-600" />;
  }
};

/**
 * ErrorMessage Component
 * Displays error messages with appropriate styling and actions
 *
 * @component
 * @example
 * // Basic usage with retry
 * <ErrorMessage
 *   statusCode={500}
 *   message="Server error occurred"
 *   onRetry={() => refetch()}
 * />
 *
 * @example
 * // Network error with compact variant
 * <ErrorMessage
 *   statusCode={0}
 *   variant="compact"
 *   onRetry={() => refetch()}
 * />
 *
 * @example
 * // 404 error
 * <ErrorMessage
 *   statusCode={404}
 *   variant="inline"
 * />
 */
export const ErrorMessage: React.FC<ErrorMessageProps> = ({
  statusCode,
  message,
  onRetry,
  isRetrying = false,
  className,
  variant = 'default',
  errorCode,
}) => {
  const errorMessage = getErrorMessage(statusCode, message, errorCode);
  const icon = getErrorIcon(statusCode);

  // Inline variant - minimal display
  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center gap-2 rounded-md bg-red-50 p-3', className)}>
        {icon}
        <span className="text-sm text-red-700">{errorMessage}</span>
      </div>
    );
  }

  // Compact variant - medium display with optional retry
  if (variant === 'compact') {
    return (
      <div
        className={cn(
          'flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 p-4',
          className
        )}
        role="alert"
        aria-live="assertive"
      >
        <div className="flex items-start gap-3">
          {icon}
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            disabled={isRetrying}
            className="self-start"
            aria-label="Retry loading"
          >
            {isRetrying ? 'Retrying...' : 'Try Again'}
          </Button>
        )}
      </div>
    );
  }

  // Default variant - full featured alert
  return (
    <div
      className={className}
      role="alert"
      aria-live="assertive"
    >
      <Alert variant="destructive">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">{icon}</div>
          <div className="flex-1">
            <AlertTitle className="text-base font-semibold">
              {statusCode === 404 ? 'Product Not Found' : 'Error Loading'}
            </AlertTitle>
            <AlertDescription className="mt-2">
              <p className="text-sm">{errorMessage}</p>
              {onRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRetry}
                  disabled={isRetrying}
                  className="mt-3 gap-2"
                  aria-label="Retry loading"
                >
                  {isRetrying ? 'Retrying...' : 'Try Again'}
                </Button>
              )}
            </AlertDescription>
          </div>
        </div>
      </Alert>
    </div>
  );
};

export default ErrorMessage;
